import { useEffect, useRef } from 'react'

interface VoiceAgentFaceProps {
    isConnected: boolean
    isSpeaking: boolean
    status?: 'disconnected' | 'connecting' | 'connected' | (string & {})
    getFrequencyData: () => Uint8Array | null
}

const NUM_SPECTRUM = 32

function clamp(value: number, min: number, max: number) {
    return Math.min(max, Math.max(min, value))
}

export function VoiceAgentFace({
    isConnected,
    isSpeaking,
    status,
    getFrequencyData,
}: VoiceAgentFaceProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const animationFrameRef = useRef<number>()
    const lastRenderMsRef = useRef<number>(0)
    const bassRef = useRef(0)
    const spectrumBarsRef = useRef<number[]>(new Array(NUM_SPECTRUM).fill(0))
    const prefersReducedMotionRef = useRef(false)
    const getFrequencyDataRef = useRef(getFrequencyData)
    const stateRef = useRef({
        isConnected,
        isSpeaking,
        status: status ?? (isConnected ? 'connected' : 'disconnected')
    })
    const viewportRef = useRef({ width: 400, height: 100, dpr: 1 })

    useEffect(() => {
        stateRef.current.isConnected = isConnected
        stateRef.current.isSpeaking = isSpeaking
        stateRef.current.status = status ?? (isConnected ? 'connected' : 'disconnected')
    }, [isConnected, isSpeaking, status])

    useEffect(() => {
        getFrequencyDataRef.current = getFrequencyData
    }, [getFrequencyData])

    useEffect(() => {
        const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)')
        if (!mq) return
        const onChange = () => { prefersReducedMotionRef.current = mq.matches }
        onChange()
        mq.addEventListener?.('change', onChange)
        return () => mq.removeEventListener?.('change', onChange)
    }, [])

    useEffect(() => {
        const el = containerRef.current
        const canvas = canvasRef.current
        if (!el || !canvas) return

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const applySize = () => {
            const rect = el.getBoundingClientRect()
            const nextW = Math.max(1, Math.floor(rect.width))
            const nextH = Math.max(1, Math.floor(rect.height))
            const nextDpr = clamp(window.devicePixelRatio || 1, 1, 2)

            viewportRef.current = { width: nextW, height: nextH, dpr: nextDpr }

            canvas.style.width = `${nextW}px`
            canvas.style.height = `${nextH}px`
            canvas.width = Math.floor(nextW * nextDpr)
            canvas.height = Math.floor(nextH * nextDpr)
        }

        applySize()

        const ro = new ResizeObserver(() => applySize())
        ro.observe(el)

        const render = (frameMs: number) => {
            const reduceMotion = prefersReducedMotionRef.current
            if (reduceMotion && frameMs - lastRenderMsRef.current < 55) {
                animationFrameRef.current = requestAnimationFrame(render)
                return
            }
            lastRenderMsRef.current = frameMs

            const { width, height, dpr } = viewportRef.current
            const cx = width / 2

            ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
            ctx.clearRect(0, 0, width, height)

            const { isConnected: isConnectedNow, isSpeaking: isSpeakingNow, status: statusNow } = stateRef.current
            const time = frameMs * 0.001

            const offMode = !isConnectedNow && statusNow !== 'connecting'

            if (offMode) {
                bassRef.current = 0
                for (let i = 0; i < NUM_SPECTRUM; i++) spectrumBarsRef.current[i] = 0
            }

            const frequencyData = getFrequencyDataRef.current?.() ?? null

            let bassValue = 0
            if (frequencyData && frequencyData.length > 0 && isSpeakingNow) {
                const bassSlice = Math.floor(frequencyData.length / 8)
                for (let i = 0; i < bassSlice; i++) bassValue += frequencyData[i] / 255
                bassValue /= bassSlice
            }
            bassRef.current += (bassValue - bassRef.current) * (reduceMotion ? 0.12 : 0.15)

            // Spectrum bar values — frequency-driven when speaking, ambient breathing otherwise
            for (let i = 0; i < NUM_SPECTRUM; i++) {
                let targetValue = 0
                if (!offMode) {
                    if (isSpeakingNow && frequencyData && frequencyData.length > 0) {
                        const freqIndex = Math.floor((i / NUM_SPECTRUM) * frequencyData.length)
                        targetValue = frequencyData[freqIndex] / 255
                    } else if (isConnectedNow || statusNow === 'connecting') {
                        targetValue = 0.08 + Math.sin(time * 1.2 + i * 0.25) * 0.06
                    }
                }
                spectrumBarsRef.current[i] += (targetValue - spectrumBarsRef.current[i]) * (reduceMotion ? 0.12 : 0.18)
            }

            // Subtle background glow below bars
            if (!offMode) {
                const glowY = height * 0.95
                const glow = ctx.createRadialGradient(cx, glowY, 0, cx, glowY, width * 0.5)
                if (isSpeakingNow) {
                    glow.addColorStop(0, `rgba(168, 85, 247, ${0.12 + bassRef.current * 0.10})`)
                    glow.addColorStop(0.6, `rgba(236, 72, 153, ${0.07 + bassRef.current * 0.04})`)
                } else {
                    glow.addColorStop(0, 'rgba(6, 182, 212, 0.10)')
                    glow.addColorStop(0.6, 'rgba(99, 102, 241, 0.05)')
                }
                glow.addColorStop(1, 'transparent')
                ctx.fillStyle = glow
                ctx.fillRect(0, 0, width, height)
            }

            // Spectrum bars
            {
                const barWidth = Math.max(3, Math.floor(width / (NUM_SPECTRUM * 1.7)))
                const gap = Math.max(2, Math.floor(barWidth * 0.55))
                const totalW = NUM_SPECTRUM * (barWidth + gap) - gap
                const left = cx - totalW / 2
                const floorY = height * 0.92
                const maxBarH = height * 0.78

                ctx.save()
                ctx.globalAlpha = offMode ? 0 : (isSpeakingNow ? 0.88 : 0.60)

                for (let i = 0; i < NUM_SPECTRUM; i++) {
                    const barVal = spectrumBarsRef.current[i]
                    const barH = Math.max(2, barVal * maxBarH)
                    const x = left + i * (barWidth + gap)
                    const y = floorY - barH
                    const hue = isSpeakingNow ? 300 + (i / NUM_SPECTRUM) * 50 : 188
                    const lightness = 55 + barVal * 20
                    ctx.fillStyle = `hsl(${hue}, 70%, ${lightness}%)`

                    ctx.beginPath()
                    const ctxAny = ctx as CanvasRenderingContext2D & { roundRect?: (...args: unknown[]) => void }
                    if (ctxAny.roundRect) {
                        ctxAny.roundRect(x, y, barWidth, barH, [2, 2, 0, 0])
                    } else {
                        ctx.rect(x, y, barWidth, barH)
                    }
                    ctx.fill()
                }

                ctx.restore()
            }

            animationFrameRef.current = requestAnimationFrame(render)
        }

        animationFrameRef.current = requestAnimationFrame(render)

        return () => {
            ro.disconnect()
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
        }
    }, [])

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full select-none"
        >
            <canvas
                ref={canvasRef}
                className="absolute inset-0"
                style={{ background: 'transparent' }}
                role="img"
                aria-label="Voice agent visualizer"
            />
        </div>
    )
}
